'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, GraduationCap } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

type LoginFormData = {
  email: string;
  password: string;
  rememberMe: boolean;
};

type SignupFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
};

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);

  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', rememberMe: false } });
  const signupForm = useForm<SignupFormData>({ defaultValues: { firstName: '', lastName: '', email: '', password: '', confirmPassword: '', terms: false } });

  const onLogin = async (data: LoginFormData) => {
    setLoginLoading(true);
    setLoginError(null);

    const lower = data.email.toLowerCase().trim();

    // Admin emails should use admin portal
    const adminEmails = [
      'admin@eugenetechrn.io', 'superadmin@eugenetechrn.io', 'eugene@eugenetechrn.io',
      'dr.chen@eugenetechrn.io', 'j.okafor@eugenetechrn.io', 'a.mensah@eugenetechrn.io',
      'k.asante@eugenetechrn.io', 'f.rashid@eugenetechrn.io', 's.boateng@eugenetechrn.io',
    ];
    if (adminEmails.includes(lower)) {
      setLoginError('Admin accounts must use the admin portal. Use the link below.');
      setLoginLoading(false);
      return;
    }

    try {
      await signIn(data.email, data.password);
      router.push('/learner-dashboard');
      router.refresh();
    } catch (err: any) {
      setLoginError(err?.message || 'Invalid email or password. Please check your credentials and try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const onSignup = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setSignupLoading(true);
    setSignupError(null);
    try {
      await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`,
      });
      router.push('/learner-dashboard');
      router.refresh();
    } catch (err: any) {
      setSignupError(err?.message || 'Could not create account. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="fade-in">
      {/* Mobile logo */}
      <div className="flex items-center gap-2 mb-6 lg:hidden">
        <AppLogo size={36} />
        <span className="font-bold text-xl text-foreground">EugeneTechRN</span>
      </div>

      {/* Tab switcher */}
      <div className="flex bg-muted rounded-2xl p-1 mb-8">
        {(['login', 'signup'] as const).map((t) => (
          <button
            key={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
              tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        ))}
      </div>

      {tab === 'login' ? (
        <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
            <p className="text-muted-foreground text-sm">Sign in to access your courses and learning materials</p>
          </div>

          {/* Learner badge */}
          <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl border bg-emerald-500/10 border-emerald-500/25">
            <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
            <span className="text-emerald-400 flex items-center gap-1.5 text-sm font-semibold">
              <GraduationCap size={14} />
              Student Portal
            </span>
            <span className="text-muted-foreground text-xs flex-1">— Access courses &amp; learning materials</span>
          </div>

          {/* Email field */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
            <input
              type="email"
              className="input-field"
              placeholder="you@example.com"
              {...loginForm.register('email', { required: 'Email is required' })}
            />
            {loginForm.formState.errors.email && (
              <p className="text-xs text-danger mt-1.5">{loginForm.formState.errors.email.message}</p>
            )}
          </div>

          {/* Password field */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input-field pr-11"
                placeholder="Enter your password"
                {...loginForm.register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="text-xs text-danger mt-1.5">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" {...loginForm.register('rememberMe')} className="w-4 h-4 rounded border-border text-primary focus:ring-ring" />
              <span className="text-sm text-muted-foreground">Remember me</span>
            </label>
            <button type="button" className="text-sm text-primary font-medium hover:underline">
              Forgot password?
            </button>
          </div>

          {loginError && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">
              {loginError}{' '}
              {loginError.includes('admin portal') && (
                <Link href="/admin-login" className="underline font-semibold text-violet-400">
                  Go to Admin Login →
                </Link>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loginLoading}
            className="btn-primary w-full py-3 btn-press"
          >
            {loginLoading ? (
              <><Loader2 size={16} className="animate-spin" /> Signing in...</>
            ) : (
              'Sign In'
            )}
          </button>

          <p className="text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <button type="button" onClick={() => setTab('signup')} className="text-primary font-semibold hover:underline">
              Create one free
            </button>
          </p>

          <p className="text-center text-xs text-muted-foreground pt-1">
            Admin or Facilitator?{' '}
            <Link href="/admin-login" className="text-primary font-semibold hover:underline">
              Use the Admin Portal
            </Link>
          </p>
        </form>
      ) : (
        <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">Start learning today</h2>
            <p className="text-muted-foreground text-sm">Create your free EugeneTechRN learner account</p>
          </div>

          <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-start gap-2.5">
            <GraduationCap size={15} className="text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-300/80">
              <span className="font-semibold text-emerald-300">Learner accounts only.</span> Facilitator and Admin accounts are created by the Super Admin.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">First name</label>
              <input type="text" className="input-field" placeholder="Jane" {...signupForm.register('firstName', { required: true })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Last name</label>
              <input type="text" className="input-field" placeholder="Smith" {...signupForm.register('lastName', { required: true })} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
            <input type="email" className="input-field" placeholder="you@example.com" {...signupForm.register('email', { required: true })} />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input-field pr-11"
                placeholder="Create a password"
                {...signupForm.register('password', { required: true, minLength: 8 })}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Confirm password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                className="input-field pr-11"
                placeholder="Repeat your password"
                {...signupForm.register('confirmPassword', { required: true })}
              />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signupForm.formState.errors.confirmPassword && (
              <p className="text-xs text-danger mt-1.5">{signupForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" {...signupForm.register('terms', { required: true })} className="w-4 h-4 mt-0.5 rounded border-border text-primary focus:ring-ring flex-shrink-0" />
            <span className="text-sm text-muted-foreground">
              I agree to the{' '}
              <button type="button" className="text-primary hover:underline font-medium">Terms of Service</button>
              {' '}and{' '}
              <button type="button" className="text-primary hover:underline font-medium">Privacy Policy</button>
            </span>
          </label>

          {signupError && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">
              {signupError}
            </div>
          )}

          <button type="submit" disabled={signupLoading} className="btn-primary w-full py-3 btn-press">
            {signupLoading ? (
              <><Loader2 size={16} className="animate-spin" /> Creating account...</>
            ) : (
              'Create Learner Account'
            )}
          </button>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <button type="button" onClick={() => setTab('login')} className="text-primary font-semibold hover:underline">
              Sign in
            </button>
          </p>
        </form>
      )}
    </div>
  );
}